import styled from 'styled-components';
import { FlexColumn, Dialog } from '@elementary/common-ui/components';
import Typography from '@mui/material/Typography';
import { ReactComponent as AlertIcon } from '@elementary/common-ui/assets/alert-warning.svg';
import { DuplicateCloudTest } from 'shared/types';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { pluralize } from 'humanize-plus';
import { fontSize, radius, space } from '@elementary/common-ui/style/utils';

const Bold = styled(Typography).attrs({ component: 'span' })`
  font-size: inherit;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const DuplicateList = styled.ul`
  padding-top: ${space('md')};
  padding-bottom: ${space('md')};
  background-color: ${({ theme }) => theme.palette.surface.emphasis};
  border-radius: ${radius('sm')};
  max-height: 200px;
  overflow-y: auto;
`;

const DuplicateItem = styled.li`
  font-size: ${fontSize('sm')};
  color: ${({ theme }) => theme.palette.content.muted};
  margin-bottom: ${space('xs')};
  word-break: break-all;

  &:last-child {
    margin-bottom: 0;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  duplicates: DuplicateCloudTest[];
  totalSubmittedTestsCount: number;
  isLoading?: boolean;
};

const formatTestType = (testType: string): string => {
  if (testType === 'volume_anomalies') return 'Volume anomalies';
  if (testType === 'freshness_anomalies') return 'Freshness anomalies';
  if (testType === 'pipeline_task_performance')
    return 'Pipeline task performance';
  if (testType === 'data_contract') return 'Data contract';
  return testType;
};

const OverrideTestsConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  duplicates,
  totalSubmittedTestsCount,
  isLoading = false,
}: Props) => {
  const duplicateCount = duplicates.length;

  return (
    <Dialog
      customWidth={600}
      open={open}
      isLoading={isLoading}
      onClose={onClose}
      title={`Override existing ${pluralizeString(
        duplicateCount,
        'test',
        'tests',
      )}`}
      onConfirmButtonClick={onConfirm}
      titleIcon={<AlertIcon />}
      cancelText="Cancel"
      confirmText="Override"
      message={
        <FlexColumn sx={{ gap: 1 }}>
          <Typography sx={{ fontSize: fontSize('sm') }}>
            <Bold>{pluralizeString(duplicateCount, 'table', 'tables')}</Bold>{' '}
            {totalSubmittedTestsCount > 1
              ? `(out of ${totalSubmittedTestsCount} selected tables) `
              : ''}
            already {pluralize(duplicateCount, 'has', 'have')} an{' '}
            <Bold>existing test</Bold>. Proceeding will override the existing{' '}
            <Bold>{pluralizeString(duplicateCount, 'test', 'tests')}</Bold> with
            the new configuration.{' '}
          </Typography>
          <Typography sx={{ fontSize: fontSize('sm') }}>
            {formatTestType(duplicates[0]?.test_type)} on the following{' '}
            {pluralize(duplicateCount, 'table', 'tables')} will be overridden:
          </Typography>
          <DuplicateList>
            {duplicates.map((duplicate, index) => (
              <DuplicateItem key={index}>
                {duplicate.database_name}.{duplicate.schema_name}.
                {duplicate.table_name}
              </DuplicateItem>
            ))}
          </DuplicateList>
        </FlexColumn>
      }
      secondaryMessage={
        'This will update the test configuration (severity, owners, tags, and anomaly settings) and trigger a new test run.'
      }
    />
  );
};

export default OverrideTestsConfirmationDialog;
